import { isEmpty, sortBy, truthyMap } from 'lib/array/arrayUtils'
import { serialiseOccupancy } from 'lib/search/occupancy'
import { SORTBY_HOME, SORTBY_SUBREGION_NSW, SORTBY_SUBREGION_VIC } from 'constants/offerList'

const stringCompare = (a: string, b: string) => a.localeCompare(b)

const HOMEPAGE_HERO_LIST_SORT_BY = new Set<string>([
  SORTBY_HOME,
  SORTBY_SUBREGION_NSW,
  SORTBY_SUBREGION_VIC,
])

function serialiseMetaSearchObject(metaSearchObject: App.MetaSearchObject) {
  return [
    `region:${metaSearchObject.region}`,
    `offerCountry:${metaSearchObject.offerCountry}`,
    `latitude:${metaSearchObject.latitude}`,
    `longitude:${metaSearchObject.longitude}`,
    `limit:${metaSearchObject.limit ?? 20}`,
    `disabled:${metaSearchObject.disabled}`,
    `offerId:${metaSearchObject.offerId}`,
  ].join(',')
}

/**
 * Offer-list identity for the current viewer. Homepage hero ranking
 * (`home` / AU NSW and VIC subregion sorts) is partitioned for LuxPlus members.
 * Charter bundle pricing is partitioned whenever that request is explicitly
 * LuxPlus-priced (including preview/spoof treatments). Every other list keeps
 * the same key as `getOfferListKey`.
 */
export function getOfferListKeyForUser(
  filters: App.OfferListFilters,
  canViewLuxPlusBenefits: boolean,
) {
  const useLuxPlusCharterPricing = filters.charterBundle && filters.isLuxPlusMember === true
  if ((!canViewLuxPlusBenefits && !useLuxPlusCharterPricing) || (!isHomepageHeroOfferList(filters) && !filters.charterBundle)) {
    const { isLuxPlusMember: _ignored, ...filtersWithoutMembership } = filters
    return getOfferListKey(filtersWithoutMembership)
  }

  return getOfferListKey({
    ...filters,
    isLuxPlusMember: true,
  })
}

function isHomepageHeroOfferList(filters: App.OfferListFilters) {
  return !!filters.sortBy && HOMEPAGE_HERO_LIST_SORT_BY.has(filters.sortBy)
}

/**
 * Creates a unique key based on the set of filters passed in
 * This is used to uniquely identify an offer list within the application
 * @param filters The filters object for the offer list
 * @returns A key representing the offer list for the filters provided
 */
export default function getOfferListKey(filters: App.OfferListFilters) {
  const filterEntries = Object.entries(filters)

  // Order is important for the key we need it to generate the same key for the same set of filters
  // even if those filters are defined on the object in a different order
  const sortedEntries = sortBy(filterEntries, entry => entry[0], 'desc')

  const keys = truthyMap(sortedEntries, ([filterType, filterValue]) => {
    // truthy check fails on numbers and we also want actual boolean falses to
    // go through as a 'false'. That means the only things we don't want are
    // undefined or null. Or NaN, please don't send a NaN
    if (!isEmpty(filterValue)) {
      switch (filterType) {
        // Display-only preference, doesn't affect which offers/prices are returned
        case 'viewPriceInPoints':
          return undefined
        case 'isLuxPlusMember':
          return filterValue === true ? `${filterType}=true` : undefined
        case 'rooms':
          return `${filterType}=${serialiseOccupancy(filterValue).join(';')}`
        case 'metaSearchObject':
          return `${filterType}=${serialiseMetaSearchObject(filterValue as App.MetaSearchObject)}`
        default:
          if (Array.isArray(filterValue) && filterValue.length > 0) {
            return (`${filterType}=` + [...filterValue].sort(stringCompare).join(','))
          }
          if (typeof filterValue !== 'object') {
            return `${filterType}=${filterValue as string}`
          }

          return undefined
      }
    }
  })

  return keys.join(';')
}
